import React, {useCallback, useMemo, useRef, useState} from 'react'
import {UseFormMethods} from 'react-hook-form'
import styled from 'styled-components'
import {
  PAYMENT_CURRENCY_ID_DEFAULT,
  PAYMENT_CURRENCY_NAME_DEFAULT,
  WINDOW_MODE_TABLET_WIDTH,
} from 'consts'
import {useTranslation} from 'i18n'
import {
  GiftShopLocationModal,
  GiftShopTemplatePriceChangeModal,
  GiftShopTemplateUploadForm,
  GiftShopTemplateZeroPriceModal,
  GiftShopUploadContentItemVideoImage,
} from 'pages'
import {GiftShopUploadFormData} from 'types'
import {requestData} from 'services'
import {
  convertSearchTreeItemToTag,
  convertTagToSearchTreeItem,
  getContentURL,
  getGiftshopLocationName,
} from 'utils'
import {
  Button,
  ButtonIcon,
  ConfirmationModal,
  ModalLoading,
} from 'common/components'
import convertUnit from 'lib/unit'
import {GiftShopTemplateDetailContentEditVideoProps} from './GiftShopTemplateDetailContentEditVideoProps'

const StyledEditContainer = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  background-color: ${({theme}) => theme.white_3};
  padding: ${convertUnit(20)};
  z-index: 2;
  overflow-y: scroll;
  scrollbar-width: auto;
  scrollbar-color: ${({theme}) => `${theme.primary_5} ${theme.white_3}`};
  ::-webkit-scrollbar {
    background-color: ${({theme}) => theme.white_3};
    width: ${convertUnit(25)};
  }
  ::-webkit-scrollbar-thumb {
    background-color: ${({theme}) => theme.primary_5};
    background-clip: content-box;
    border: ${convertUnit(8)} solid ${({theme}) => theme.white_3};
    border-radius: ${convertUnit(16)};
  }
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    background-color: ${({theme}) => theme.white_1};
  }
`

const StyledContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
`

const StyledContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  border-radius: ${convertUnit(8)};
  background-color: ${({theme}) => theme.white_1};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    flex-direction: column;
  }
`

const StyledFormContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
`

const StyledButtonsContainer = styled.div`
  display: flex;
  box-sizing: border-box;
  padding: 0 ${convertUnit(20)} ${convertUnit(20)} 0;
  gap: ${convertUnit(20)};
  width: 100%;
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    padding: 0 0 ${convertUnit(20)};
  }
`

const StyledSaveButton = styled(Button)`
  width: 100%;
`

export default function GiftShopTemplateDetailContentEditVideo({
  onDeleteCreation,
  stateData,
  stateEdit,
}: GiftShopTemplateDetailContentEditVideoProps) {
  const {translate} = useTranslation()
  const form = useRef<GiftShopUploadFormData>()
  const [currentMap, setCurrentMap] = useState<
    UseFormMethods<GiftShopUploadFormData>
  >()
  const setData = stateData[1]
  const [edit, setEdit] = stateEdit
  const priceState = useState(0)
  const userPrice = priceState[0]
  const [modal, setModal] = useState(true)
  const [loading, setLoading] = useState(false)
  const [confirmModal, setConfirmModal] = useState<
    'delete' | 'zero-price' | 'price-change'
  >()

  const handleSave = useCallback(async () => {
    if (edit && form.current) {
      setLoading(true)
      const {
        description,
        price,
        date,
        time,
        tags,
        currencyId,
        location,
        peopleTag,
      } = form.current

      const tagNicknames = {}
      if (peopleTag) {
        peopleTag.forEach((item) => {
          tagNicknames[item.nickname] = item.id
        })
      }

      if (location !== '') {
        const dateTime = new Date(
          date.concat('T').concat(time).concat(':00.000+07:00'),
        ).toISOString()
        requestData('giftshop_edit_content', {
          actionType: 'execute',
          useDefaultMessage: true,
          data: {
            content_id: edit.content_id,
            currency_id: currencyId,
            nickname: tagNicknames,
            description,
            location,
            original_at: dateTime,
            price: +price,
            tag_ids: tags.map((tag) => tag.tag_id),
            title: edit.title,
          },
          onRequestReceived: () => setLoading(false),
          onRequestSuccess: ({status, data: {result}}) => {
            if (status === 200) {
              setEdit(undefined)
              setData((previous) =>
                previous.map((item) =>
                  item.content_id === edit.content_id
                    ? {
                        ...item,
                        description,
                        tags: tags.map((tag) =>
                          convertSearchTreeItemToTag(tag),
                        ),
                        location: JSON.parse(location),
                        price: price === '0' ? 0 : +userPrice,
                        original_price: +price,
                        original_at: dateTime,
                        nickname: peopleTag
                          .map((val) => val.nickname)
                          .join(','),
                        tag_nicknames: [
                          ...peopleTag,
                          ...(result?.tag_nicknames || []),
                        ].filter((tag) => tag.id !== null),
                      }
                    : item,
                ),
              )
            }
          },
        })
      }
    }
  }, [edit, setData, setEdit, userPrice])

  const handleToggleConfirmModal = useCallback(
    () => setConfirmModal(undefined),
    [],
  )

  const handleCheckPrice = useCallback(() => {
    if (form.current?.price === '0') {
      setConfirmModal('zero-price')
      return
    }
    if (+(form.current?.price ?? 0) !== edit?.price) {
      setConfirmModal('price-change')
      return
    }
    handleSave()
  }, [edit?.price, handleSave])

  const handleRenderContent = useMemo(
    () =>
      edit ? (
        <GiftShopUploadContentItemVideoImage
          isEdit
          videoHeight={edit.resolution.height}
          videoSize={edit.size}
          videoSrc={getContentURL({url: edit.video_url, type: edit.type})}
          videoTitle={edit.title}
          videoWidth={edit.resolution.width}
        />
      ) : (
        <></>
      ),
    [edit],
  )

  const handleRenderForm = useMemo(
    () =>
      edit &&
      typeof edit.original_at === 'string' && (
        <GiftShopTemplateUploadForm
          defaultValues={{
            id: edit.content_id,
            peopleTag: edit.tag_nicknames,
            title: edit.title,
            tags: edit.tags.map((tag) => convertTagToSearchTreeItem(tag)),
            description: edit.description,
            price: edit.original_price.toString(),
            currencyId: edit.currency?.id ?? PAYMENT_CURRENCY_ID_DEFAULT,
            currencyName: edit.currency?.name ?? PAYMENT_CURRENCY_NAME_DEFAULT,
            location: getGiftshopLocationName({
              latitude: edit.location?.latitude.toString(),
              longitude: edit.location?.longitude.toString(),
              name: edit.location?.name,
            }),
            originalAt: edit.original_at,
            size: edit.size,
            width: 0,
            height: 0,
          }}
          priceState={priceState}
          showDeleteButton={false}
          onAddLocation={setCurrentMap}
          onFormUpdate={(values) => {
            form.current = values
          }}
          uploadType="video"
          toggleModal={() => setModal(true)}
        />
      ),
    [edit, priceState],
  )

  const handleRenderButtons = useMemo(
    () => (
      <StyledButtonsContainer>
        <ButtonIcon
          iconType="trash"
          iconColor="gray_3"
          hasShadow={false}
          backgroundColor="white_1"
          backgroundHoverColor="white_3"
          style={{maxWidth: convertUnit(42), maxHeight: convertUnit(42)}}
          onClick={() => setConfirmModal('delete')}
        />
        <StyledSaveButton
          label={translate('global:saveChanges')}
          onClick={handleCheckPrice}
        />
      </StyledButtonsContainer>
    ),
    [handleCheckPrice, translate],
  )

  const handleRenderLocation = useMemo(
    () => (
      <GiftShopLocationModal
        mapForm={currentMap}
        visible={modal}
        showField="giftshop"
        toggleModal={() => setModal((prev) => !prev)}
        onSave={() => setCurrentMap(undefined)}
      />
    ),
    [currentMap, modal],
  )

  const handleRenderDeleteConfirmationModal = useMemo(
    () => (
      <ConfirmationModal
        visible
        title={translate('giftShop:uploadDeleteContent')}
        text={translate('giftShop:contentDetailEditDeleteDescription')}
        onConfirm={() => onDeleteCreation && onDeleteCreation()}
        toggleModal={handleToggleConfirmModal}
        onBackdropPress={handleToggleConfirmModal}
      />
    ),
    [handleToggleConfirmModal, onDeleteCreation, translate],
  )

  const handleRenderZeroPriceModal = useMemo(
    () => (
      <GiftShopTemplateZeroPriceModal
        visible
        onConfirm={handleSave}
        toggleModal={handleToggleConfirmModal}
      />
    ),
    [handleSave, handleToggleConfirmModal],
  )

  const handleRenderPriceChangeModal = useMemo(
    () => (
      <GiftShopTemplatePriceChangeModal
        visible
        prevPrice={edit?.price ?? 0}
        newPrice={+(form.current?.price ?? 0)}
        toggleModal={handleToggleConfirmModal}
        onConfirm={handleSave}
      />
    ),
    [edit?.price, handleSave, handleToggleConfirmModal],
  )

  const handleRenderModalLoading = useMemo(
    () => <ModalLoading visible={loading} />,
    [loading],
  )

  const handleRenderConfirmModal = useMemo(() => {
    switch (confirmModal) {
      case 'delete':
        return handleRenderDeleteConfirmationModal
      case 'zero-price':
        return handleRenderZeroPriceModal
      case 'price-change':
        return handleRenderPriceChangeModal
    }
    return <></>
  }, [
    confirmModal,
    handleRenderDeleteConfirmationModal,
    handleRenderPriceChangeModal,
    handleRenderZeroPriceModal,
  ])

  return edit ? (
    <>
      {handleRenderLocation}
      {handleRenderModalLoading}
      {handleRenderConfirmModal}
      <StyledEditContainer>
        <StyledContentContainer>
          <StyledContainer>
            {handleRenderContent}
            <StyledFormContainer>
              {handleRenderForm}
              {handleRenderButtons}
            </StyledFormContainer>
          </StyledContainer>
        </StyledContentContainer>
      </StyledEditContainer>
    </>
  ) : null
}
